import { Timeseries } from '../../../core/types/timeseries.js';
import { TimeseriesRawDataPerVariant } from '../../context/TimeseriesRawData.context.js';
import { ChartVariant } from '../../types/timeseries-props.js';

/**
 * Checks whether dataset contains Band geometry data
 *
 * @param rawData - dataset
 *
 * @returns boolean
 */
export function containsBandData(
  rawData: TimeseriesRawDataPerVariant,
): boolean {
  return rawData['band']?.length > 0;
}

/**
 * Checks whether dataset contains Line, Area or Bar geometry data
 * @param rawData -
 *
 * @returns boolean
 */
export function containsSeriesData(
  rawData: TimeseriesRawDataPerVariant,
): boolean {
  return (
    rawData['line']?.length > 0 ||
    rawData['area']?.length > 0 ||
    rawData['bar']?.length > 0
  );
}

/**
 * Gets maximum number of names that a series have
 *
 * @param rawData - series
 *
 * @returns number
 */
export function getNameCount(rawData: TimeseriesRawDataPerVariant): number {
  return (Object.keys(rawData) as ChartVariant[]).reduce(
    (nameCount, variant: ChartVariant) => {
      const variantNameCount = rawData[variant as ChartVariant].reduce(
        (nameCount: number, singleSeries: Timeseries) =>
          Math.max(
            nameCount,
            Array.isArray(singleSeries.name) ? singleSeries.name.length : 1,
          ),
        0,
      );

      return Math.max(nameCount, variantNameCount);
    },
    0,
  );
}

/**
 * Generates a name column for each name, and the series column
 *
 * @param nameCount - number of name columns
 *
 * @returns name columns
 */
export function generateNameColumns(nameCount: number): string[] {
  const names: Array<string> = [];

  for (let i = 1; i <= nameCount; i++) {
    names.push(`dimension-${i}`);
  }

  names.push('series');

  return names;
}

/**
 * Generate all CSV columns for a given set of series
 *
 * @param rawData - series
 * @param nameCount - number of name columns
 *
 * @returns all columns
 */
export function generateColumns(
  rawData: TimeseriesRawDataPerVariant,
  nameCount: number,
): string[] {
  const columns = ['unit', 'start', 'end'];

  if (containsSeriesData(rawData)) {
    columns.push('value');
  }

  if (containsBandData(rawData)) {
    columns.push('y0');
    columns.push('y1');
  }

  const names = generateNameColumns(nameCount);

  return names.concat(columns);
}
